import React, {useEffect, useState} from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css"

import {BreadCrumbTrail} from "../../../../common/breadcrumb-trail/BreadcrumbTrail";
import facilityIcon from "../../../../../resources/images/facility-icon.png";
import {NextButton} from "../../../../common/button/next-button/NextButton";
import {BackButton} from "../../../../common/button/back-button/BackButton";
import {retrieveAllTopicAreas} from "../../../../../services/TopicAreaService";
import {retrieveAllIndicatorsForTopicArea} from "../../../../../services/IndicatorService";
import {retrieveAllStaffMembers} from "../../../../../services/StaffMemberService";
import {ToolTip} from "../../../../common/tool-tip/ToolTip";
import {retrieveAllGrantsForSystemUser} from "../../../../../services/GrantService";
import {CommentToolTip} from "../../../../common/tool-tip-comments/CommentToolTip";
import {StandardButton} from "../../../../common/button/standard-button/StandardButton";
import {createPersonDropdownDisplayValue, sortPersonList} from "../../../../../util/PersonUtil";

export const TopicAreaOne = ({context, setContext, navigateTo, navigateBack, showToaster, setSpinnerText, toolTipsEnabled}) => {
    const [topicArea, setTopicArea] = useState({externalReference: null, topicArea: "Select..."});
    const [indicator, setIndicator] = useState({externalReference: null, indicator: "Select..."});
    const [dateOfVisit, setDateOfVisit] = useState(null);
    const [staffMember, setStaffMember] = useState({externalReference: null, displayValue: "Select..."});
    const [grant, setGrant] = useState({externalReference: null, name: "Select..."});
    const [topicAreaList, setTopicAreaList] = useState([]);
    const [indicatorList, setIndicatorList] = useState([]);
    const [staffMemberList, setStaffMemberList] = useState([]);
    const [grantList, setGrantList] = useState(null);

    useEffect(() => {
        if (!context.viewMode) {
            setContext((prevState) => ({
                ...prevState,
                breadcrumbs: {
                    ...prevState.breadcrumbs,
                    facilityBreadcrumb: {
                        text: context.facilityScreen.locationBreadCrumbText,
                        type: "Location",
                        screenKey: "facilitySelect",
                    },
                    topicAreaBreadcrumb: {
                        ...prevState.breadcrumbs.topicAreaBreadcrumb,
                        type: "Topic Area",
                        screenKey: "topicAreaOne",
                    }
                },
            }));
        } else {
            if (!context.copyOfTopicAreaScreen) {
                const copyOfTopicAreaScreen = JSON.stringify(context.topicAreaScreen);
                setContext((prevState) => ({
                    ...prevState,
                    copyOfTopicAreaScreen,
                }));
            }
        }
    }, []);

    const fetchGrants = async () => {
        const [data] = await retrieveAllGrantsForSystemUser(context.username, context.loggedInUser.externalReference);
        if (data) {
            if (data.responseStatus === "Success") {
                const filteredGrants = data.grantVOS.filter((grant) => grant.active);
                setGrantList(filteredGrants);
            }
        }
    }

    const fetchTopicAreas = async () => {
        const [data] = await retrieveAllTopicAreas(context.username);
        if (data) {
            if (data.responseStatus === "Success") {
                const activeTopicAreas = data.topicAreaVOList.filter((topicArea) => topicArea.active);
                setTopicAreaList(activeTopicAreas);
            }
        }
    }

    const fetchIndicators = async (topicAreaExternalReference) => {
        const [data] = await retrieveAllIndicatorsForTopicArea(context.username, topicAreaExternalReference);
        if (data) {
            if (data.responseStatus === "Success") {
                const indicatorList = [];
                const resultMap = data.resultMap;
                for (const property in resultMap) {
                    indicatorList.push(resultMap[property]);
                }
                const activeIndicators = indicatorList.filter((indicator) => indicator.active);
                setIndicatorList(activeIndicators);
            }
        }
    }

    const fetchStaffMembers = async () => {
        const [data] = await retrieveAllStaffMembers(context.username);
        if (data) {
            if (data.responseStatus === "Success") {
                const staffMembers = data.systemUserVOList.map((staffMember) => {
                    return {
                        ...staffMember,
                        displayValue: createPersonDropdownDisplayValue(staffMember, true, true),
                    }
                });
                const activeStaffMembers = staffMembers.filter((staffMember) => staffMember.active);
                sortPersonList(activeStaffMembers);
                setStaffMemberList(activeStaffMembers);
            }
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            setSpinnerText("Retrieving Grants");
            await fetchGrants();
            setSpinnerText("Retrieving Topic Areas");
            await fetchTopicAreas();
            setSpinnerText("Retrieving Staff Members");
            await fetchStaffMembers();
            setSpinnerText(null);
        }
        fetchData();
    }, []);

    useEffect(() => {
        if (topicArea.externalReference) {
            const fetchData = async () => {
                setSpinnerText("Retrieving Indicators");
                await fetchIndicators(topicArea.externalReference);
                setSpinnerText(null);
            }
            fetchData();
        }

        if (!context.viewMode) {
            setIndicator({externalReference: null, indicator: "Select..."});
        }
    }, [topicArea]);

    useEffect(() => {
        if (staffMemberList.length > 0 && !staffMember.externalReference) {
            const {loggedInUser} = context;
            const defaultStaffMember = staffMemberList.filter((staffMember) => staffMember.externalReference === loggedInUser.externalReference)[0];
            const displayValue = createPersonDropdownDisplayValue(defaultStaffMember, true, true);
            setStaffMember({externalReference: defaultStaffMember.externalReference, displayValue});
        }
    }, [staffMemberList]);

    useEffect(() => {
        const topicAreaScreen = context.topicAreaScreen;
        if (topicAreaScreen) {
            if (topicAreaScreen.grant) {
                setGrant(topicAreaScreen.grant);
            }

            if (topicAreaScreen.topicArea) {
                setTopicArea(topicAreaScreen.topicArea);
            }

            if (topicAreaScreen.indicator) {
                setIndicator(topicAreaScreen.indicator);
            }

            if (topicAreaScreen.dateOfVisit) {
                setDateOfVisit(new Date(topicAreaScreen.dateOfVisit));
            }

            if (topicAreaScreen.staffMember) {
                setStaffMember(topicAreaScreen.staffMember);
            }
        }
    }, [context]);

    const handleNextClick = () => {
        if (!context.viewMode) {
            const {roles} = context;
            if (roles.includes("comprehensive-display")) {
                setContext((prevState) => ({
                    ...prevState,
                    topicAreaScreen: {
                        ...prevState.topicAreaScreen,
                        grant,
                        topicArea,
                        indicator,
                        dateOfVisit,
                        staffMember,
                    }
                }));
                navigateTo("topicAreaTwo");
            } else {
                setContext((prevState) => ({
                    ...prevState,
                    topicAreaScreen: {
                        ...prevState.topicAreaScreen,
                        grant,
                        topicArea,
                        indicator,
                        dateOfVisit,
                        staffMember,
                        contactPersonList: [],
                        accompanyingPersonList: [],
                    }
                }));
                navigateTo("topicAreaFour");
            }
        } else {
            if (!context.roles.includes("comprehensive-display")) {
                setContext((prevState) => ({
                    ...prevState,
                    topicAreaScreen: {
                        ...prevState.topicAreaScreen,
                        grant,
                        topicArea,
                        indicator,
                        dateOfVisit,
                        staffMember,
                        contactPersonList: [],
                        accompanyingPersonList: [],
                    }
                }));
                navigateTo("topicAreaFour");
            } else {
                if (!context.topicAreaScreen.contactPersonList || context.topicAreaScreen.contactPersonList.length === 0) {
                    setContext((prevState) => ({
                        ...prevState,
                        topicAreaScreen: {
                            ...prevState.topicAreaScreen,
                            grant,
                            topicArea,
                            indicator,
                            dateOfVisit,
                            staffMember,
                            contactPersonList: [],
                            accompanyingPersonList: [],
                        }
                    }));
                    navigateTo("topicAreaFour");
                } else {
                    setContext((prevState) => ({
                        ...prevState,
                        topicAreaScreen: {
                            ...prevState.topicAreaScreen,
                            grant,
                            topicArea,
                            indicator,
                            dateOfVisit,
                            staffMember,
                        }
                    }));
                    navigateTo("topicAreaTwo");
                }
            }
        }
    }

    const isButtonDisabled = () => {
        return (!grant.externalReference || !topicArea.externalReference || !indicator.externalReference || !dateOfVisit || !staffMember.externalReference);
    }

    return (
        <div className="wizard-content-container">
            <div className="wizard-main-content-container">
                <div className="wizard-left-container">
                    <div className="wizard-icon-container">
                        <img src={facilityIcon} alt="Aurum Logo" className="wizard-icon"/>
                    </div>
                    <p className="wizard-icon-label">{context.facilityScreen.locationBreadCrumbText}</p>
                </div>
                <div className="wizard-right-container">
                    <div className="input-container">
                        <div>
                            <label>Grant:</label>
                            {toolTipsEnabled &&
                            <ToolTip tooltipExternalReference={"tagra123"}
                                     context={context}
                                     setSpinnerText={setSpinnerText} showToaster={showToaster}
                                     fieldName={"Grant:"}/>}
                        </div>
                        <Select options={grantList}
                                getOptionLabel={(grant) => grant.name}
                                getOptionValue={(grant) => grant.externalReference}
                                onChange={(selectedGrant) => {
                                    setGrant(selectedGrant)
                                }}
                                isDisabled={!grantList || !context.editMode}
                                value={{externalReference: grant.externalReference, name: grant.name}}
                                classNamePrefix="select"
                        />
                    </div>
                    <div className="input-container">
                        <div>
                            <label>Topic Area:</label>
                            {toolTipsEnabled &&
                            <ToolTip tooltipExternalReference={"tatopicarea123"}
                                     context={context}
                                     setSpinnerText={setSpinnerText} showToaster={showToaster}
                                     fieldName={"Topic Area:"}/>}
                        </div>
                        <Select options={topicAreaList}
                                getOptionLabel={(topicArea) => topicArea.topicArea}
                                getOptionValue={(topicArea) => topicArea.externalReference}
                                onChange={(selectedTopicArea) => {
                                    setTopicArea(selectedTopicArea)
                                }}
                                isDisabled={!topicAreaList || !context.editMode}
                                value={{externalReference: topicArea.externalReference, topicArea: topicArea.topicArea}}
                                classNamePrefix="select"
                        />
                    </div>
                    <div className="input-container">
                        <div>
                            <label>Indicator:</label>
                            {toolTipsEnabled &&
                            <ToolTip tooltipExternalReference={"taindicator123"}
                                     context={context}
                                     setSpinnerText={setSpinnerText} showToaster={showToaster}
                                     fieldName={"Indicator:"}/>}
                        </div>
                        <Select options={indicatorList}
                                getOptionLabel={(indicator) => indicator.indicator}
                                getOptionValue={(indicator) => indicator.externalReference}
                                onChange={(selectedIndicator) => {
                                    setIndicator(selectedIndicator)
                                }}
                                isDisabled={!indicatorList || !context.editMode}
                                value={{externalReference: indicator.externalReference, indicator: indicator.indicator}}
                                classNamePrefix="select"
                        />
                    </div>
                    <div className="input-container">
                        <div>
                            <label>Date of Visit:</label>
                            {toolTipsEnabled &&
                            <ToolTip tooltipExternalReference={"tadateofvis123"}
                                     context={context}
                                     setSpinnerText={setSpinnerText} showToaster={showToaster}
                                     fieldName={"Date of Visit:"}/>}
                        </div>
                        <DatePicker selected={dateOfVisit} className="text-input" dateFormat='dd/MM/yyyy'
                                    showPopperArrow={false} disabled={!context.editMode} placeholderText="Select..."
                                    onChange={(selectedDate) => {
                                        setDateOfVisit(selectedDate);
                                    }}
                        />
                    </div>
                    <div className="input-container">
                        <div>
                            <label>Staff Member:</label>
                            {toolTipsEnabled &&
                            <ToolTip tooltipExternalReference={"tastaffmember123"}
                                     context={context}
                                     setSpinnerText={setSpinnerText} showToaster={showToaster}
                                     fieldName={"Staff Member:"}/>}
                        </div>
                        <Select options={staffMemberList}
                                getOptionLabel={(staffMember) => staffMember.displayValue}
                                getOptionValue={(staffMember) => staffMember.externalReference}
                                onChange={(selectedStaffMember) => {
                                    setStaffMember(selectedStaffMember);
                                }}
                                isDisabled={!staffMemberList || !context.editMode}
                                value={{
                                    externalReference: staffMember.externalReference,
                                    displayValue: staffMember.displayValue
                                }}
                                classNamePrefix="select"
                        />
                    </div>
                    {context.viewMode &&
                    <CommentToolTip contextName={"Visit"} showToaster={showToaster} setSpinnerText={setSpinnerText}
                                    contextExternalReference={context.topicAreaScreen.visitExternalReference}
                                    context={context} toolTipsEnabled={toolTipsEnabled}/>}
                    {context.viewMode && <div className="back-to-overview-button-container">
                        <StandardButton text="Problem Overview" onClick={() => {
                            navigateTo("problemTree");
                        }}/>
                    </div>}
                    <div className="navigation-button-container">
                        <NextButton disabled={isButtonDisabled()} onClick={handleNextClick}/>
                        <BackButton onClick={navigateBack}/>
                    </div>
                </div>
            </div>

            <div className="wizard-breadcrumb-container">
                <BreadCrumbTrail context={context} navigateTo={navigateTo}/>
            </div>
        </div>
    );
}
